import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/button/default";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { IResvInf, IRsvLodInf } from "interface/mypage";
import {
  alertControl,
  fullDateFormat,
  getDurationDate,
  priceFormat,
  successControl,
  timeFormat,
} from "util/commAction";
import { useAuthContext } from "components/context/authContext";
import { useMutation } from "@tanstack/react-query";
import { sendResvMailApi } from "components/api/mypage";
import { IOutletContext } from "./layout";

interface IMyBookCard {
  resvInf: any | (IResvInf & Partial<IRsvLodInf>);
  lodVisible: boolean;
}

interface IProps extends IMyBookCard {
  onClick(): void;
}

const BookCardWeb = ({ resvInf, lodVisible, onClick }: IProps) => {
  const { t } = useTranslation();
  const { resvStat } = useOutletContext<IOutletContext>();
  const { lodInf } = useAuthContext();

  const duaration = getDurationDate(resvInf?.rmChkInDt, resvInf?.rmChkOutDt);

  //이메일 발송
  const sendEmlMutation = useMutation({
    mutationFn: sendResvMailApi,
    onSuccess: (res) => {
      if (res) {
        successControl({
          status: res.status,
          success: () => {
            alertControl({
              isOpen: true,
              inner: t("txt_send_eml_done_mpg"),
            });
          },
        });
      }
    },
  });

  const handleSendEml = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    alertControl({
      isOpen: true,
      inner: t("txt_send_eml_mpg"),
      cncl_btn: true,
      click: () => {
        sendEmlMutation.mutate(resvInf.rmResvNo);
      },
    });
  };

  return (
    <div className={"book-card web"} onClick={onClick}>
      <div className="flex justify-between">
        <div className="flex gap-[18px]">
          <h6>{t("txt_rsr_nmb_cmm")}</h6>
          <h6>{resvInf?.rmResvNo}</h6>
          <h6>{t("txt_rsr_dt_cmm")}</h6>
          <h6>{fullDateFormat(resvInf?.resvDt)}</h6>
        </div>
        <h6 data-icon={resvStat} className="text-blue-300 font-font-B">
          {resvStat === "wait"
            ? t("sb_ttl_1_mpg")
            : resvStat === "done"
            ? t("sb_ttl_2_mpg")
            : t("sb_ttl_3_mpg")}
        </h6>
      </div>

      <div>
        <h2>{resvInf?.rmTypNm}</h2>
        <div className="rsv-cnt">{duaration}</div>
      </div>
      <div className="book-inf mt-[18px]">
        <ul>
          <li className="flex flex-col gap-2">
            <div className="flex">
              <h5>{t("txt_chck_in_cmm")}</h5>
              <h5>
                {fullDateFormat(resvInf?.rmChkInDt)}&nbsp;/&nbsp;
                {timeFormat((resvInf?.rmChkInTi || lodInf?.chkInTi) ?? "--")}
              </h5>
            </div>
            <div className="flex">
              <h5>{t("txt_chck_out_cmm")}</h5>
              <h5>
                {fullDateFormat(resvInf?.rmChkOutDt)}&nbsp;/&nbsp;
                {timeFormat((resvInf?.rmChkOutTi || lodInf?.chkOutTi) ?? "--")}
              </h5>
            </div>
          </li>
        </ul>
        <h2 className="text-blue-300">{priceFormat(resvInf?.resvAmt)}</h2>
      </div>

      {lodVisible && (
        <div className="flex jsutify-between items-end">
          <div className="flex-1 flex flex-col gap-2">
            <h6 data-icon="addr">
              {resvInf?.lodAddr1}
              {resvInf?.lodAddr2} {resvInf?.lodAddr3} {resvInf?.lodAddr4}
            </h6>
            <h6 data-icon="phone">
              {resvInf?.lodTelCntyCd}-{resvInf?.lodTelNo}
            </h6>
            <h6 data-icon="home">
              <a href={resvInf?.hpUrl} target="_blank">
                {resvInf?.hpUrl}
              </a>
            </h6>
          </div>
          <Button
            text={t("btn_eml_mpg")}
            styels="icon-mail radius-4 bg-white"
            onClick={handleSendEml}
          />
        </div>
      )}
    </div>
  );
};

const BookCardMb = ({ resvInf, lodVisible, onClick }: IProps) => {
  const { t } = useTranslation();
  const { resvStat } = useOutletContext<IOutletContext>();
  const { lodInf } = useAuthContext();

  const range = getDurationDate(resvInf?.rmChkInDt, resvInf?.rmChkOutDt);

  //이메일 발송
  const sendEmlMutation = useMutation({
    mutationFn: sendResvMailApi,
    onSuccess: (res) => {
      if (res) {
        successControl({
          status: res.status,
          success: () => {
            alertControl({
              isOpen: true,
              inner: t("txt_send_eml_done_mpg"),
            });
          },
        });
      }
    },
  });

  const handleSendEml = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    alertControl({
      isOpen: true,
      inner: t("txt_send_eml_mpg"),
      cncl_btn: true,
      click: () => {
        sendEmlMutation.mutate(resvInf.rmResvNo);
      },
    });
  };

  return (
    <div className={"book-card mb"} onClick={onClick}>
      <div className="">
        <div className="flex gap-2">
          <h6 className="w-[50px]">{t("txt_rsr_nmb_cmm")}</h6>
          <h6>{resvInf?.rmResvNo}</h6>
        </div>
        <div className="flex gap-2">
          <h6 className="w-[50px]">{t("txt_rsr_dt_cmm")}</h6>
          <h6>{fullDateFormat(resvInf?.resvDt)}</h6>
        </div>
      </div>
      <div>
        <div className="flex justify-between items-end gap-2 mb-3">
          <h2 className="ellipsis ">{resvInf?.rmTypNm}</h2>
          <h6
            data-icon={resvStat}
            className={
              "font-font-B relative top-2 -right-3 min-w-24 " +
              " " +
              (resvStat === "wait"
                ? "text-blue-300 "
                : resvStat === "cancel"
                ? "text-red-200"
                : "")
            }
          >
            {resvStat === "wait"
              ? t("sb_ttl_1_mpg")
              : resvStat === "done"
              ? t("sb_ttl_2_mpg")
              : t("sb_ttl_3_mpg")}
          </h6>
        </div>
        <div className="flex justify-between items-top">
          <div className="rsv-cnt">{range}</div>
          <h2 className="text-blue-300 relative bottom-2">
            {priceFormat(resvInf?.resvAmt)}
          </h2>
        </div>
      </div>
      <div className="book-inf mt-3">
        <ul className="">
          <li className="flex items-start leading-6">
            <h5>{t("txt_chck_in_cmm")}</h5>
            <h5>
              {fullDateFormat(resvInf?.rmChkInDt)}&nbsp;/&nbsp;
              {timeFormat(resvInf?.rmChkInTi ?? lodInf?.chkInTi)}
            </h5>
          </li>
          <li className="flex leading-6">
            <h5>{t("txt_chck_out_cmm")}</h5>
            <h5>
              {fullDateFormat(resvInf?.rmChkOutDt)}&nbsp;/&nbsp;
              {timeFormat(resvInf?.rmChkOutTi ?? lodInf?.chkOutTi)}
            </h5>
          </li>

          {lodVisible && (
            <li className="flex justify-end">
              <Button
                text={t("btn_eml_mpg")}
                styels="icon-mail radius-4 bg-white"
                onClick={handleSendEml}
              />
            </li>
          )}
        </ul>
        {lodVisible && (
          <div className="text-left w-full border-tp-dashed pt-4">
            <h6 data-icon="addr">
              {resvInf?.lodAddr1}
              {resvInf?.lodAddr2} {resvInf?.lodAddr3} {resvInf?.lodAddr4}
            </h6>
            <h6 data-icon="phone">
              {resvInf?.lodTelCntyCd}-{resvInf?.lodTelNo}
            </h6>
            <h6 data-icon="home">
              <a href={resvInf?.hpUrl} target="_blank">
                {resvInf?.hpUrl}
              </a>
            </h6>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BookCard({ resvInf, lodVisible }: IMyBookCard) {
  const navigate = useNavigate();
  const { rmPdNo } = useParams();

  const handleClick = () => {
    if (!rmPdNo) {
      return navigate(`${resvInf?.rmResvNo}`);
    }
    return;
  };

  return (
    <div>
      <BookCardWeb
        resvInf={resvInf}
        lodVisible={lodVisible}
        onClick={handleClick}
      />
      <BookCardMb
        resvInf={resvInf}
        lodVisible={lodVisible}
        onClick={handleClick}
      />
    </div>
  );
}
